import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';

const categoryNameMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryNameMap[c.slug] = c.name;
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [filteredComics, setFilteredComics] = useState(comics);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter((c) => c.categories.includes(activeCategory)));
    }
  }, [activeCategory]);

  const currentCategoryName = categoryNameMap[activeCategory] || '全部';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${currentCategoryName}韩国漫画 - 韩漫阅读`,
      description: `浏览${currentCategoryName}题材的韩国耽美漫画，无删减下拉式免费在线阅读。`,
      url: `https://hanman.life/category/${activeCategory}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '韩漫阅读',
        url: 'https://hanman.life/',
      },
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategoryName}韩国漫画 - 韩漫阅读`}
        description={`韩漫阅读为您提供${currentCategoryName}题材的韩国耽美漫画，无删减下拉式免费在线阅读。精选优质${currentCategoryName}韩漫，持续更新中。`}
        keywords={`${currentCategoryName}韩国漫画,${currentCategoryName}耽美漫画,韩国漫画,无删减漫画,免费韩漫在线阅读`}
        structuredData={structuredData}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${currentCategoryName}漫画` },
          ]}
        />

        <section className="px-4 md:px-6 py-6 md:py-10">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
            {currentCategoryName}漫画
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            共 {filteredComics.length} 部{currentCategoryName !== '全部' ? currentCategoryName : ''}漫画作品
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
              <Link
                to="/category/all"
                className="mt-4 inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回全部漫画
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}